import React from 'react';

// Material-UI
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridOverlay,
} from '@mui/x-data-grid';
import Grid from '@mui/material/Grid';

// Components
import WaiverFilter from './WaiverFilter';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  HTTPHeaders,
  MemberWaiverStatus
} from "../../interfaces";

import {
  API_URL,
  NULL_DATETIME_STRING
} from '../../global';

// UTC to local date formatting hooks
import { useDates } from '../../hooks/UseDates';
// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

const WaiverList: React.FC = (props) => {

  const { dateStringToLocalDateFormatted } = useDates();
  const { getAccessToken } = useAuth0User();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [filteredList, setFilteredList] = React.useState<Array<MemberWaiverStatus>>([]);
  const [waiverList, setWaiverList] = React.useState<Array<MemberWaiverStatus>>([]);

  // #region Styles

  const theme = useTheme();
  const styles = {
    gridContainer: {
      padding: theme.spacing(1, 0, 1, 0),
      height: 600,
    },
    noRows: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: theme.spacing(2, 0, 2, 0),
    },
  };

  // #endregion

  // #region Methods

  /*
* Grid column definitions
*/
  const columns = (): Array<GridColDef> => {

    return [
      {
        field: 'memberName',
        headerName: 'Member',
        width: 175,
        type: 'string',
        align: 'left'
      },
      {
        field: 'status',
        headerName: 'Category',
        width: 150,
        type: 'string',
        align: 'left',
      },
      {
        field: 'waiverStatus',
        headerName: 'Waiver Status',
        width: 125,
        type: 'string',
        align: 'center'
      },
      {
        field: 'waiverDate',
        headerName: 'Signed',
        width: 210,
        type: 'string',
        align: 'left',
        renderCell: (params: GridCellParams) => (
          renderDate(params)
        )
      }
    ];

  }

  /*
* Fetch request passes as a list
*/
  const fetchWaiverList = async () => {

    setIsLoading(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    await fetch(`${API_URL}/v1/members/waivers`,
      {
        method: 'GET',
        cache: 'no-cache',
        headers: headers,
        mode: 'cors'
      })
      .then((response) => {
        if (!response.ok) {
          setIsLoading(false);
          throw new Error('Member waivers not retrieved');
        }
        return response.json()
      })
      .then((e: Array<MemberWaiverStatus>) => {
        setWaiverList(e);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
        throw new Error('Member waivers not retrieved');
      });
  }

  /*
 * Overlay displayed when no data
 */
  const noRowsOverlay = () => {
    return (
      <GridOverlay>
        <div style={styles.noRows}>No member waivers to display</div>
      </GridOverlay>
    );
  }

  /*
* Render date column
*/
  const renderDate = (params: GridCellParams) => {
    return (
      params.value.includes(NULL_DATETIME_STRING) ? 'n/a' : dateStringToLocalDateFormatted(params.value, 'EEE MM/dd/yyyy HH:mm:ss')
    );
  }

  // #endregion

  // #region Event handlers

  /*
* Handler for waiver dialog window
*/
  const handleWaiverFilter = (option: string) => {
    if (option === '99') {
      setFilteredList(waiverList);
    } else {
      const filtered = waiverList.filter((w: MemberWaiverStatus) => w.waiverStatusID.toString() === option);
      setFilteredList(filtered);
    }
  }

  React.useEffect(() => {
    if (waiverList.length > 0) {
      setFilteredList(waiverList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiverList.length]);

  React.useEffect(() => {
    fetchWaiverList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #endregion

  return (
    <Grid container size={12} justifyContent="center">
      <Grid size={{ sm: 3, lg: 2 }}>
        <WaiverFilter
          handleChange={handleWaiverFilter}
          waivers={waiverList}
        />
      </Grid>
      <Grid size={{ sm: 5, lg: 5 }} sx={styles.gridContainer}>
        <DataGrid
          columns={columns()}
          slots={{
            noRowsOverlay: noRowsOverlay
          }}
          density="compact"
          disableColumnSelector
          disableRowSelectionOnClick
          getRowId={(row) => row.memberID}
          loading={isLoading}
          rows={filteredList}
        />
      </Grid>
    </Grid>
  );

}

export default WaiverList;